import { inject, Injectable, signal } from '@angular/core';
import {
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signOut,
  type User,
} from 'firebase/auth';

import { FIREBASE_AUTH } from '../firebase/firebase.tokens';

const EMAIL_KEY = 'emailForSignIn';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth = inject(FIREBASE_AUTH);
  readonly user = signal<User | null>(null);
  private readonly authReadyPromise: Promise<void>;

  constructor() {
    this.authReadyPromise = new Promise((resolve) => {
      let resolved = false;
      onAuthStateChanged(this.auth, (u) => {
        this.user.set(u);
        if (!resolved) {
          resolved = true;
          resolve();
        }
      });
    });
  }

  waitForAuthReady(): Promise<void> {
    return this.authReadyPromise;
  }

  isAuthenticated(): boolean {
    return this.auth.currentUser !== null;
  }

  async sendMagicLink(email: string): Promise<void> {
    const actionCodeSettings = {
      url: `${window.location.origin}/login/complete`,
      handleCodeInApp: true,
    };
    await sendSignInLinkToEmail(this.auth, email, actionCodeSettings);
    localStorage.setItem(EMAIL_KEY, email);
  }

  async completeMagicLink(href: string, email: string): Promise<void> {
    if (!isSignInWithEmailLink(this.auth, href)) {
      throw new Error('Invalid sign-in link');
    }
    await signInWithEmailLink(this.auth, email, href);
    localStorage.removeItem(EMAIL_KEY);
  }

  getStoredEmail(): string | null {
    return localStorage.getItem(EMAIL_KEY);
  }

  async logout(): Promise<void> {
    await signOut(this.auth);
  }
}
